import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom"; // Import useParams
import { useSelector, useDispatch } from "react-redux";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomInput from "../../../components/common/CustomInput/CustomInput";
import { listTripByPaginationApiCall } from "../../../Api/api";
import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import { getDriverDropdownDataAsync } from "../../../redux/features/fleetMaster";
import MISReport from "./MISReport";
import { ROLES } from "../../../constansts/Roles";
import TripTable from "./TripTable";
import { useDebounce } from "../../../Hooks/useDebounce";

export default function ContractualTripList() {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const trip_status = searchParams.get("trip_status");
   const review_status = searchParams.get("review_status");

   const [records, setRecords] = useState([]);
   const [listPagination, setListPagination] = useState(null);
   const [limit, setLimit] = useState(10);
   const [page, setPage] = useState(0);
   const [searchTerm, setSearchTerm] = useState("");

   const debouncedSearchTerm = useDebounce(searchTerm, 500);

   const userRoleId = useSelector((state) => state?.auth?.user?.data?.user?.role_id);
   const userId = useSelector((state) => state?.auth?.user?.data?.user?.id);

   let roleName;
   for (const key in ROLES) {
      if (ROLES[key] === userRoleId) {
         roleName = key;
      }
   }

   const fetchTrips = useCallback(
      async (search = "") => {
         if (!roleName || !userId) return;

         dispatch(backdropLoadingAction(true));

         try {
            const { data: response } = await listTripByPaginationApiCall(
               roleName,
               userId,
               limit,
               page,
               trip_status,
               review_status,
               search,
            );
            if (response.statusCode === 200) {
               setRecords(response?.data);
               setListPagination(response?.pagination);
            }
         } catch (error) {
            toast.error("Failed to fetch trips.");
         } finally {
            dispatch(backdropLoadingAction(false));
         }
      },
      [limit, page, roleName, userId, review_status, trip_status, dispatch],
   );

   useEffect(() => {
      fetchTrips(debouncedSearchTerm);
   }, [fetchTrips, debouncedSearchTerm]);

   useEffect(() => {
      dispatch(getDriverDropdownDataAsync());
   }, [dispatch]);

   const handleStatusFilter = (filterName, status) => {
      const newParams = new URLSearchParams(searchParams);
      if (status) {
         newParams.set(filterName, status);
      } else {
         newParams.delete(filterName);
      }
      setSearchParams(newParams);
      setPage(0); // Reset to first page when filters change
   };

   useEffect(() => {
      setPage(0); // Only once when debounced search term changes
   }, [debouncedSearchTerm]);

   const handleFilter = (e) => {
      const searchText = e.target.value.toLowerCase();

      setSearchTerm(searchText);
   };

   return (
      <div>
         <BodyHeader title="Trip List" />

         <div className="px-4 py-3">
            <Box
               sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  mb: 3,
                  gap: 2,
                  flexWrap: "wrap",
               }}
            >
               <Stack direction="row" alignItems="center" spacing={3}>
                  <Box>
                     <Typography
                        variant="caption"
                        sx={{
                           display: "block",
                           mb: 0.5,
                           fontWeight: 600,
                           color: "text.secondary",
                           textTransform: "uppercase",
                           letterSpacing: "0.5px",
                        }}
                     >
                        Trip Status
                     </Typography>
                     <ToggleButtonGroup
                        size="small"
                        color="primary"
                        value={trip_status || ""}
                        exclusive
                        onChange={(_, value) => handleStatusFilter("trip_status", value)}
                        aria-label="Trip Status"
                        sx={{
                           backgroundColor: "#fff",
                           "& .MuiToggleButton-root": {
                              px: 2,
                              py: 0.5,
                              textTransform: "none",
                              fontWeight: 500,
                           },
                        }}
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="open">Open</ToggleButton>
                        <ToggleButton value="closed">Closed</ToggleButton>
                     </ToggleButtonGroup>
                  </Box>
                  <Box>
                     <Typography
                        variant="caption"
                        sx={{
                           display: "block",
                           mb: 0.5,
                           fontWeight: 600,
                           color: "text.secondary",
                           textTransform: "uppercase",
                           letterSpacing: "0.5px",
                        }}
                     >
                        Review Status
                     </Typography>
                     <ToggleButtonGroup
                        size="small"
                        color="primary"
                        value={review_status || ""}
                        exclusive
                        onChange={(_, value) => handleStatusFilter("review_status", value)}
                        aria-label="Review Status"
                        sx={{
                           backgroundColor: "#fff",
                           "& .MuiToggleButton-root": {
                              px: 2,
                              py: 0.5,
                              textTransform: "none",
                              fontWeight: 500,
                           },
                        }}
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="approved">Approved</ToggleButton>
                        <ToggleButton value="rejected">Rejected</ToggleButton>
                     </ToggleButtonGroup>
                  </Box>
               </Stack>

               <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ minWidth: 250 }}>
                     <CustomInput
                        inputType="text"
                        placeholder="Search by vehicle, driver..."
                        id="search"
                        value={searchTerm}
                        onChange={(e) => handleFilter(e)}
                        containerStyle={{ margin: 0 }}
                     />
                  </Box>
                  {roleName === "supervisor" && (
                     <button
                        className="btn btn-primary px-4 shadow-sm"
                        style={{ height: "38px", whiteSpace: "nowrap" }}
                        onClick={() => navigate("/trip/contractual/add")}
                        type="button"
                     >
                        + Create Trip
                     </button>
                  )}
                  <MISReport />
               </Stack>
            </Box>

            <TripTable
               rows={records}
               limit={limit}
               page={page}
               setLimit={setLimit}
               setPage={setPage}
               listPagination={listPagination}
            />
         </div>
      </div>
   );
}
